<ng-container *ngIf="merchant">
    <div class="audit-controls">
        <div class="audit-filters">
            <div class="filter-group">
                <a class="filter-pill"
                   [class.filter-pill--active]="channel === 'whatsapp'"
                   (click)="setChannel('whatsapp')">
                    <i class="fab fa-whatsapp"></i>
                    WhatsApps
                    <span class="filter-count">{{ filteredWhatsapps.length }}</span>
                </a>
                <a class="filter-pill"
                   [class.filter-pill--active]="channel === 'email'"
                   (click)="setChannel('email')">
                    <i class="fa-solid fa-envelope"></i>
                    Emails
                    <span class="filter-count">{{ filteredEmails.length }}</span>
                </a>
            </div>

            <div class="filter-group">
                <a *ngFor="let opt of dateRangeOptions"
                   class="filter-pill filter-pill--sm"
                   [class.filter-pill--active]="selectedDays === opt.days"
                   (click)="setDays(opt.days)">
                    {{ opt.label }}
                </a>
            </div>
        </div>

        <button type="button" class="btn-refresh" (click)="refresh()" [disabled]="loading">
            <i class="fa-solid fa-arrows-rotate" [class.fa-spin]="loading"></i>
            Refresh
        </button>
    </div>

    <div *ngIf="error" class="audit-alert audit-alert--error">
        <i class="fa-solid fa-circle-exclamation"></i>
        {{ error }}
    </div>

    <div *ngIf="loading" class="audit-loading">
        <i class="fa-solid fa-circle-notch fa-spin"></i> Loading audit logs...
    </div>

    <!-- WhatsApps -->
    <div *ngIf="channel === 'whatsapp'">
        <div *ngIf="!loading && filteredWhatsapps.length === 0" class="audit-empty">
            No WhatsApp sends recorded in the selected period.
        </div>

        <div class="table-responsive" *ngIf="filteredWhatsapps.length > 0">
            <table class="audit-table">
                <thead>
                    <tr>
                        <th style="width: 170px">Sent at</th>
                        <th>Template</th>
                        <th>Recipient</th>
                        <th style="width: 120px">Status</th>
                        <th style="width: 120px">Subscription</th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let log of filteredWhatsapps">
                        <tr (click)="toggleExpand(log.$key)" class="audit-row">
                            <td class="cell-nowrap">
                                <ng-container *ngIf="log.createdAt && log.createdAt.toDate">
                                    {{ log.createdAt.toDate() | date: 'dd MMM yyyy HH:mm' }}
                                </ng-container>
                            </td>
                            <td>
                                <code class="template-code">{{ log.templateName }}</code>
                            </td>
                            <td>
                                +{{ log.phoneNumber }}
                                <span *ngIf="log.appEnv && log.appEnv !== 'production'" class="env-badge">
                                    {{ log.appEnv }}
                                </span>
                            </td>
                            <td>
                                <span [class]="statusBadgeClass(log.status)">{{ log.status || 'unknown' }}</span>
                            </td>
                            <td>
                                <a *ngIf="log.subscriptionRef" class="sub-link" [routerLink]="['/merchant', merchant.$key, 'subscription', log.subscriptionRef.id, 'view']">
                                    {{ log.subscriptionRef.id | slice: 0:8 }}...
                                </a>
                            </td>
                        </tr>
                        <tr *ngIf="expandedKey === log.$key">
                            <td colspan="5" class="expand-cell">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <strong>Broadcast id:</strong>
                                        <div><code>{{ log.providerBroadcastId || '—' }}</code></div>
                                    </div>
                                    <div class="col-md-6">
                                        <strong>Message id:</strong>
                                        <div><code>{{ log.providerMessageId || '—' }}</code></div>
                                    </div>
                                    <div *ngIf="log.errorMessage" class="col-12">
                                        <strong class="error-label">Error:</strong>
                                        <div class="error-text">{{ log.errorMessage }}</div>
                                    </div>
                                    <div *ngIf="log.parameters && log.parameters.length" class="col-12">
                                        <strong>Template parameters:</strong>
                                        <ul class="mb-0">
                                            <li *ngFor="let p of log.parameters">
                                                <code>{{ p.name }}</code>: {{ p.value }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </ng-container>
                </tbody>
            </table>
            <div class="audit-footer">Showing the most recent {{ filteredWhatsapps.length }} of up to 100 WhatsApp logs.</div>
        </div>
    </div>

    <!-- Emails -->
    <div *ngIf="channel === 'email'">
        <div *ngIf="!loading && filteredEmails.length === 0" class="audit-empty">
            No emails recorded in the selected period.
        </div>

        <div class="table-responsive" *ngIf="filteredEmails.length > 0">
            <table class="audit-table">
                <thead>
                    <tr>
                        <th style="width: 170px">Sent at</th>
                        <th>Subject</th>
                        <th>Recipients</th>
                        <th style="width: 120px">Status</th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let log of filteredEmails">
                        <tr (click)="toggleExpand(log.$key)" class="audit-row">
                            <td class="cell-nowrap">
                                <ng-container *ngIf="log.createdAt && log.createdAt.toDate">
                                    {{ log.createdAt.toDate() | date: 'dd MMM yyyy HH:mm' }}
                                </ng-container>
                            </td>
                            <td>{{ log.subject }}</td>
                            <td>
                                <ng-container *ngIf="log.toEmails && log.toEmails.length > 0; else noRecipients">
                                    <span *ngFor="let to of log.toEmails; let isLast = last">
                                        {{ to }}<span *ngIf="!isLast">, </span>
                                    </span>
                                </ng-container>
                                <ng-template #noRecipients>
                                    <span class="no-recipients">no recipients</span>
                                </ng-template>
                            </td>
                            <td>
                                <span [class]="statusBadgeClass(log.status)">{{ log.status || 'unknown' }}</span>
                            </td>
                        </tr>
                        <tr *ngIf="expandedKey === log.$key">
                            <td colspan="4" class="expand-cell">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <strong>Template id:</strong>
                                        <div><code>{{ log.templateId || '—' }}</code></div>
                                    </div>
                                    <div *ngIf="log.errorMessage" class="col-12">
                                        <strong class="error-label">Error:</strong>
                                        <div class="error-text">{{ log.errorMessage }}</div>
                                    </div>
                                    <div class="col-12">
                                        <strong>Open full record:</strong>
                                        <a [routerLink]="['/merchant', merchant.$key, 'email', log.$key]" class="sub-link ms-2">
                                            View email
                                        </a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </ng-container>
                </tbody>
            </table>
        </div>
    </div>
</ng-container>
